import { useState, useRef, useEffect, useCallback } from 'react';
import { useChatHistory, ChatSession } from '../hooks/useChatHistory';
import { api } from '../api/client';

export function ChatPage() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [showDocSelector, setShowDocSelector] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const [showReasoning, setShowReasoning] = useState(false);
  const [socraticMode, setSocraticMode] = useState(false);
  const [cognitiveHandshake, setCognitiveHandshake] = useState(false);

  // Active Bot Persona from localStorage
  const [activeBot, setActiveBot] = useState<any | null>(() => {
    try {
      const saved = localStorage.getItem('active_bot_persona');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  // Privacy Guard Scanner & Redactor
  const [privacyWarning, setPrivacyWarning] = useState<{ detected: string[]; originalText: string; redactedText: string } | null>(null);

  const scanSensitiveData = (text: string) => {
    const detected: string[] = [];
    let redacted = text;

    const phoneRegex = /(?:\+84|0)(?:3|5|7|8|9)[0-9]{8}\b/g;
    if (phoneRegex.test(text)) {
      detected.push('Số điện thoại');
      redacted = redacted.replace(phoneRegex, '[REDACTED_PHONE]');
    }

    const cardRegex = /\b(?:\d{4}[ -]?){3}\d{4}\b/g;
    if (cardRegex.test(text)) {
      detected.push('Số thẻ ngân hàng');
      redacted = redacted.replace(cardRegex, '[REDACTED_CARD]');
    }

    const keyRegex = /\b(?:sk-[a-zA-Z0-9_-]{16,}|ghp_[a-zA-Z0-9]{16,})\b/g;
    if (keyRegex.test(text)) {
      detected.push('API Key / Secret Token');
      redacted = redacted.replace(keyRegex, '[REDACTED_KEY]');
    }

    const cccdRegex = /\b(?:\d{9}|\d{12})\b/g;
    if (cccdRegex.test(text)) {
      detected.push('Số CCCD / CMND');
      redacted = redacted.replace(cccdRegex, '[REDACTED_CCCD]');
    }

    return { hasSensitive: detected.length > 0, detected, redacted };
  };

  // EricSpace: Stream / Insert into OnlyOffice
  const streamToOnlyOffice = async (textToInsert: string) => {
    try {
      const res = await fetch('http://127.0.0.1:8765/api/office/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'INSERT_TEXT', text: textToInsert }),
        signal: AbortSignal.timeout(2500)
      });
      if (res.ok) {
        alert('✅ Đã chèn thành công nội dung này vào OnlyOffice trên PC!');
      } else {
        alert('⚠️ Không thể chèn: Đảm bảo bạn đã mở một tài liệu trong OnlyOffice.');
      }
    } catch (e) {
      alert('Không kết nối được EricSpace Bridge (http://127.0.0.1:8765).\nVui lòng khởi động Erix_ComputeUse.exe trên máy tính.');
    }
  };

  const startVoice = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert('Trình duyệt không hỗ trợ nhận diện giọng nói. Hãy dùng Chrome.'); return; }
    const rec = new SR();
    rec.lang = 'vi-VN';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e: any) => {
      const transcript = e.results[0][0].transcript;
      setInput(prev => prev ? prev + ' ' + transcript : transcript);
      setIsListening(false);
    };
    rec.onerror = () => setIsListening(false);
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
    rec.start();
    setIsListening(true);
  };

  const stopVoice = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  const [voiceMcpLoading, setVoiceMcpLoading] = useState(false);

  const handleErixVoiceQuery = async () => {
    const textToSend = input.trim();
    if (!textToSend || loading || voiceMcpLoading) return;
    setInput('');
    setVoiceMcpLoading(true);
    try {
      const res = await api.queryVoice('send_voice_message', { text: textToSend, sender: 'ErixOne User' });
      if (res.ok) {
        alert('🎙️ Đã phát lệnh thoại tới Erix Voice MCP!\n\nQuota còn lại: ' + (res.quota ? `${res.quota.used}/${res.quota.limit === -1 ? '∞' : res.quota.limit}` : 'Hợp lệ'));
      } else {
        alert('Lỗi: ' + (res.error || 'Không thành công'));
      }
    } catch (e: any) {
      alert('Lỗi Erix Voice: ' + (e.message || 'Không thể kết nối. Vui lòng kiểm tra cấu hình Admin.'));
    } finally {
      setVoiceMcpLoading(false);
    }
  };

  const {
    conversations,
    activeId,
    createConversation,
    updateMessages
  } = useChatHistory();

  const activeConv = conversations.find(c => c.id === activeId);
  const currentMessages = activeConv?.messages ?? [];
  const selectedDocIds = activeConv?.selectedDocumentIds ?? [];

  // Load documents for RAG selection
  useEffect(() => {
    setLoadingDocs(true);
    api.listDocuments()
      .then(res => setDocuments(res.documents ?? []))
      .catch(e => console.error('Failed to load documents for selector', e))
      .finally(() => setLoadingDocs(false));
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentMessages]);

  const handleDocToggle = (docId: string) => {
    if (!activeId) {
      // Create conv first
      const newId = createConversation([docId]);
      return;
    }
    const newSelected = selectedDocIds.includes(docId)
      ? selectedDocIds.filter(id => id !== docId)
      : [...selectedDocIds, docId];
    
    updateMessages(activeId, currentMessages, newSelected);
  };

  const autoHeight = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  const executeSend = async (textToSend: string) => {
    setLoading(true);
    if (inputRef.current) inputRef.current.style.height = '44px';

    let currentSessionId = activeId;
    let currentConv = activeConv;

    if (!currentSessionId) {
      currentSessionId = createConversation(selectedDocIds, textToSend);
      currentConv = {
        id: currentSessionId,
        title: textToSend.length > 30 ? textToSend.substring(0, 30) + '...' : textToSend,
        selectedDocumentIds: selectedDocIds,
        messages: [],
        created_at: new Date().toISOString()
      };
    }

    const tempId = `msg_${Date.now()}`;
    const initialMessages: ChatSession[] = [
      ...currentMessages,
      {
        id: tempId,
        prompt: textToSend,
        state: 'SEARCHING',
        finalResult: '',
      }
    ];

    updateMessages(currentSessionId, initialMessages, selectedDocIds);

    try {
      let queryText = textToSend;

      // Active Bot Persona injection
      if (activeBot) {
        queryText = `[Persona: ${activeBot.name} (${activeBot.role})]\nChỉ dẫn Persona: ${activeBot.system_prompt}\n\nNhiệm vụ: ${queryText}`;
      }

      // Cognitive Handshake (3-bước)
      if (cognitiveHandshake) {
        queryText = `[Cognitive Handshake] Hãy phân tích và trả lời theo ĐÚNG 3 bước nhận thức sau:\n### 🎯 Bước 1: Làm Rõ Ý Định (Clarify Intent)\n[Xác nhận mục tiêu cốt lõi và tiêu chí đánh giá]\n\n### 📋 Bước 2: Giả Thuyết & Lộ Trình (Hypothesis & Plan)\n[Đưa ra 2-3 hướng tiếp cận hoặc các bước xử lý logic]\n\n### 🚀 Bước 3: Thực Thi & Kết Luận (Execution & Synthesis)\n[Chi tiết giải pháp, số liệu trích xuất từ tài liệu và kết luận hoàn chỉnh]\n\nNội dung cần xử lý: ${queryText}`;
      }

      if (socraticMode) {
        queryText = `[Chế độ Socratic] Phân tích câu hỏi sau. Nếu chưa đủ rõ, hãy hỏi ngược 1-2 câu làm rõ trước khi trả lời đầy đủ: ${queryText}`;
      }
      if (showReasoning) {
        queryText = `Hãy trả lời theo format: <reasoning>\n[Lý luận từng bước chi tiết]\n</reasoning>\n\n**Kết luận:** [Câu trả lời cuối cùng súc tích]\n\nCâu hỏi: ${queryText}`;
      }

      const result = await api.query(queryText, selectedDocIds.length > 0 ? selectedDocIds : undefined);

      const finalMessages = initialMessages.map(m => m.id === tempId ? {
        ...m,
        state: 'DONE',
        finalResult: result.answer || result.context || 'Không tìm thấy ngữ cảnh phù hợp từ tài liệu của bạn.',
        sources: result.sources,
        latency_ms: result.latency_ms,
        routed_tier: result.routed_tier
      } : m);

      updateMessages(currentSessionId, finalMessages, selectedDocIds);
    } catch (err: any) {
      const finalMessages = initialMessages.map(m => m.id === tempId ? {
        ...m,
        state: 'ERROR',
        finalResult: '',
        error: err.message ?? 'Đã xảy ra lỗi khi thực hiện RAG query.'
      } : m);
      updateMessages(currentSessionId, finalMessages, selectedDocIds);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;

    // Check Privacy Guard
    const { hasSensitive, detected, redacted } = scanSensitiveData(text);
    if (hasSensitive) {
      setPrivacyWarning({ detected, originalText: text, redactedText: redacted });
      return;
    }

    setInput('');
    executeSend(text);
  };

  return (
    <div style={{ display: 'flex', height: '100%', gap: '20px', position: 'relative' }}>
      
      {/* Main Chat Area */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', minWidth: 0 }}>
        
        {/* Page Header */}
        <div className="page-header" style={{ flexShrink: 0 }}>
          <div>
            <h1 className="page-title">RAG Chat Query</h1>
            <p className="page-subtitle">
              {selectedDocIds.length === 0 
                ? 'Đang tìm kiếm trên toàn bộ tài liệu thư viện' 
                : `Đang tìm kiếm giới hạn trên ${selectedDocIds.length} tài liệu đã chọn`}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {activeBot && (
              <div className="badge badge--purple" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', fontSize: '12px' }}>
                <span>{activeBot.avatar}</span>
                <span>{activeBot.name}</span>
                <button
                  onClick={() => { setActiveBot(null); localStorage.removeItem('active_bot_persona'); }}
                  style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}
                  title="Hủy Persona"
                >
                  ✕
                </button>
              </div>
            )}
            <button
              className="btn btn--secondary"
              onClick={() => setCognitiveHandshake(!cognitiveHandshake)}
              style={{
                background: cognitiveHandshake ? 'rgba(16,185,129,0.2)' : undefined,
                borderColor: cognitiveHandshake ? '#10b981' : undefined,
                color: cognitiveHandshake ? '#34d399' : undefined
              }}
              title="Quy trình nhận thức 3 bước: Làm rõ ý -> Lên giả thuyết -> Thực thi"
            >
              {cognitiveHandshake ? '🤝 Handshake: ON' : '🤝 Handshake'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => setShowReasoning(!showReasoning)}
              style={{ background: showReasoning ? 'rgba(124,58,237,0.2)' : undefined, borderColor: showReasoning ? 'var(--accent-primary)' : undefined }}
              title="Hiển thị lý luận AI (chain-of-thought)"
            >
              {showReasoning ? '🧠 Lý Luận: ON' : '🧠 Lý Luận'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => setSocraticMode(!socraticMode)}
              style={{ background: socraticMode ? 'rgba(6,182,212,0.2)' : undefined, borderColor: socraticMode ? 'var(--accent-cyan)' : undefined }}
              title="Chế độ Socratic: AI hỏi ngược để làm rõ câu hỏi của bạn"
            >
              {socraticMode ? '🤔 Socratic: ON' : '🤔 Socratic'}
            </button>
            <button 
              className="btn btn--secondary" 
              onClick={() => setShowDocSelector(!showDocSelector)}
            >
              📂 Chọn tài liệu ({selectedDocIds.length})
            </button>
          </div>
        </div>

        {/* Chat History Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {currentMessages.length === 0 ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              opacity: 0.6,
              textAlign: 'center',
              padding: '40px'
            }}>
              <span style={{ fontSize: '48px' }}>🔍</span>
              <div>
                <h3 style={{ marginBottom: '6px' }}>Bắt đầu hỏi đáp RAG</h3>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '400px' }}>
                  Nhập câu hỏi của bạn dưới đây. Hệ thống sẽ tự động tìm kiếm ngữ cảnh, đối chiếu tài liệu và trích xuất câu trả lời chính xác kèm nguồn trích dẫn.
                </p>
              </div>
            </div>
          ) : (
            currentMessages.map(msg => (
              <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* User query bubble */}
                <div style={{
                  alignSelf: 'flex-end',
                  maxWidth: '75%',
                  padding: '10px 14px',
                  background: 'var(--gradient-main)',
                  borderRadius: '14px 14px 4px 14px',
                  fontSize: '13.5px',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  boxShadow: '0 4px 15px rgba(124, 58, 237, 0.2)'
                }}>
                  {msg.prompt}
                </div>

                {/* Assistant response */}
                <div style={{
                  alignSelf: 'flex-start',
                  maxWidth: '85%',
                  width: '85%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {msg.state === 'SEARCHING' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                      <span className="spinner" style={{ width: 14, height: 14 }} />
                      <span>Đang trích xuất vector, tìm kiếm ngữ cảnh &amp; sinh câu trả lời...</span>
                    </div>
                  )}

                  {msg.error && (
                    <div style={{
                      padding: '10px 14px',
                      background: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: '8px',
                      color: '#f87171',
                      fontSize: '12.5px'
                    }}>
                      ⚠️ {msg.error}
                    </div>
                  )}

                  {msg.finalResult && (
                    <div style={{
                      background: 'var(--bg-glass)',
                      border: '1px solid var(--border)',
                      borderRadius: '12px',
                      padding: '16px',
                      fontSize: '13.5px',
                      lineHeight: 1.7,
                      color: 'var(--text-primary)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {msg.finalResult}

                      {/* Display citations / sources */}
                      {msg.sources && msg.sources.length > 0 && (
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '12px',
                          borderTop: '1px solid rgba(255,255,255,0.06)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Nguồn trích dẫn ({msg.sources.length})
                          </span>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {msg.sources.map((src, idx) => (
                              <div 
                                key={idx}
                                className="badge badge--blue"
                                title={`Độ tương thích (Cosine Score): ${Math.round(src.score * 100)}%`}
                                style={{ cursor: 'help', padding: '4px 10px', fontSize: '11px' }}
                              >
                                📄 {src.filename} {src.page ? `(Trang ${src.page})` : ''}
                                <span style={{ opacity: 0.6, marginLeft: '6px', fontSize: '9px', fontFamily: 'var(--font-mono)' }}>
                                  {(src.score * 100).toFixed(0)}%
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Message Actions */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => streamToOnlyOffice(msg.finalResult)}
                          title="Chèn văn bản này trực tiếp vào OnlyOffice đang mở trên máy tính"
                          style={{ fontSize: '11px', color: 'var(--accent-cyan)', padding: '3px 8px' }}
                        >
                          ✍️ Stream vào OnlyOffice
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {msg.routed_tier && (
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                background: msg.routed_tier.includes('edge') ? 'rgba(56, 189, 248, 0.12)' : msg.routed_tier.includes('fly') ? 'rgba(168, 85, 247, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                                color: msg.routed_tier.includes('edge') ? '#38bdf8' : msg.routed_tier.includes('fly') ? '#c084fc' : 'var(--text-muted)'
                              }}
                              title={`Tầng thực thi: ${msg.routed_tier}`}
                            >
                              {msg.routed_tier.includes('edge') ? '⚡ Edge AI' : msg.routed_tier.includes('fly') ? '🚀 Fly.io T1' : '🛡️ AI Gateway'}
                            </span>
                          )}
                          {msg.latency_ms && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                              {msg.latency_ms}ms
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            ))
          )}
          <div ref={endRef} />
        </div>

        {/* Input box */}
        <div style={{
          flexShrink: 0,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'flex-end',
          gap: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          marginBottom: '10px'
        }}>
          <textarea
            ref={inputRef}
            className="textarea"
            rows={1}
            value={input}
            onChange={e => {
              setInput(e.target.value);
              autoHeight(e.target);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Hỏi bất kỳ điều gì từ tài liệu của bạn..."
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              padding: '8px 0',
              minHeight: '24px',
              maxHeight: '200px',
              resize: 'none',
              fontSize: '13.5px',
              lineHeight: 1.5,
              outline: 'none',
              boxShadow: 'none'
            }}
          />
          <button
            onClick={isListening ? stopVoice : startVoice}
            title={isListening ? 'Dừng nghe' : 'Nhập bằng giọng nói (tiếng Việt)'}
            style={{
              padding: '8px',
              borderRadius: '8px',
              background: isListening ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${isListening ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`,
              color: isListening ? '#f87171' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s',
              animation: isListening ? 'pulse-dot 1s ease-in-out infinite' : 'none'
            }}
          >
            {isListening ? '🔴' : '🎤'}
          </button>
          <button
            onClick={handleErixVoiceQuery}
            disabled={voiceMcpLoading || !input.trim()}
            title="Gửi câu lệnh này đến Erix Voice MCP để phát giọng nói"
            style={{
              padding: '8px',
              borderRadius: '8px',
              background: 'rgba(124,58,237,0.12)',
              border: '1px solid rgba(124,58,237,0.3)',
              color: '#c084fc',
              cursor: input.trim() ? 'pointer' : 'not-allowed',
              fontSize: '16px',
              transition: 'all 0.2s',
              opacity: input.trim() ? 1 : 0.4
            }}
          >
            {voiceMcpLoading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '🎙️'}
          </button>
          <button 
            className="btn btn--primary"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{ padding: '8px 16px', borderRadius: '8px' }}
          >
            {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Gửi'}
          </button>
        </div>

      </div>

      {/* Slide-out Document Selector drawer */}
      {showDocSelector && (
        <div style={{
          width: '280px',
          flexShrink: 0,
          background: 'var(--bg-surface)',
          borderLeft: '1px solid var(--border)',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: '100%',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700 }}>Kho tài liệu ngữ cảnh</h3>
            <button 
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px' }}
              onClick={() => setShowDocSelector(false)}
            >
              ✕
            </button>
          </div>

          <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
            Chọn tài liệu cụ thể để giới hạn phạm vi tìm kiếm của RAG. Nếu không chọn, hệ thống tìm toàn bộ thư viện.
          </div>

          {loadingDocs ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <div className="spinner" />
            </div>
          ) : documents.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', padding: '20px' }}>
              Thư viện trống. Hãy vào menu Documents Library để upload file trước.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {documents.map(doc => {
                const isSelected = selectedDocIds.includes(doc.id);
                return (
                  <div 
                    key={doc.id}
                    onClick={() => handleDocToggle(doc.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      background: isSelected ? 'rgba(6, 182, 212, 0.08)' : 'var(--bg-glass)',
                      border: '1px solid',
                      borderColor: isSelected ? 'var(--accent-cyan)' : 'var(--border)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '12.5px'
                    }}
                  >
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={() => {}} // handled by div onClick
                      style={{ cursor: 'pointer' }}
                    />
                    <span 
                      style={{ 
                        flex: 1, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)'
                      }}
                      title={doc.filename}
                    >
                      {doc.filename}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Privacy Guard Warning Modal */}
      {privacyWarning && (
        <div className="modal-overlay" style={{ background: 'rgba(5, 5, 12, 0.85)', backdropFilter: 'blur(8px)', zIndex: 10000, position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ maxWidth: '480px', width: '90vw', border: '1px solid rgba(239,68,68,0.4)', background: 'var(--bg-surface)', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <span style={{ fontSize: '28px' }}>🛡️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#f87171' }}>Privacy Guard — Cảnh Báo Riêng Tư</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Phát hiện dữ liệu nhạy cảm trong câu hỏi</div>
              </div>
            </div>

            <div style={{ fontSize: '13px', lineHeight: 1.6, color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Câu hỏi chứa: <strong>{privacyWarning.detected.join(', ')}</strong>.<br/>
              Để bảo vệ thông tin cá nhân và tổ chức, bạn nên che dữ liệu nhạy cảm trước khi gửi tới AI.
            </div>

            <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '12px', fontFamily: 'var(--font-mono)', marginBottom: '18px', color: '#38bdf8', wordBreak: 'break-word' }}>
              {privacyWarning.redactedText}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn--secondary btn--sm" onClick={() => setPrivacyWarning(null)}>
                Hủy bỏ
              </button>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  const orig = privacyWarning.originalText;
                  setPrivacyWarning(null);
                  setInput('');
                  executeSend(orig);
                }}
                style={{ fontSize: '12px', color: 'var(--text-muted)' }}
              >
                Gửi gốc
              </button>
              <button
                className="btn btn--primary btn--sm"
                onClick={() => {
                  const red = privacyWarning.redactedText;
                  setPrivacyWarning(null);
                  setInput('');
                  executeSend(red);
                }}
                style={{ background: 'var(--accent-green)', borderColor: 'var(--accent-green)' }}
              >
                🔒 Ẩn nhạy cảm &amp; Gửi
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
